/**
 * 工时记录相关API服务 - 支持离线缓存、错误处理和性能优化
 */
import api, { enhancedApi } from './api';
import { cacheService } from './cacheService';
import { errorService, ErrorType, ErrorLevel } from './errorService';
import {
  WorkTime,
  CreateWorkTimeData,
  UpdateWorkTimeData,
  WorkTimeQueryParams,
  WorkTimeSummary,
  ApiResponse,
  PaginatedResponse,
} from '../types';

export class WorkTimeService {
  private static readonly CACHE_TTL = 5 * 60 * 1000; // 5分钟缓存
  private static readonly SUMMARY_CACHE_TTL = 10 * 60 * 1000; // 统计数据10分钟缓存

  static async getWorkTimeList(params: WorkTimeQueryParams = {}): Promise<{ data: WorkTime[]; totalHours: number }> {
    const res = await this.getWorkTimes(params);
    const list = (res as any)?.data || [];
    let totalHours = 0;
    for (const item of list) {
      const workUnits = (item as any)?.workUnits ?? ((item as any)?.timeUnit === 'half' ? 0.5 : 1);
      const workers =
        ((item as any)?.totalWorkerCount ??
          (((item as any)?.maleWorkerCount || 0) + ((item as any)?.femaleWorkerCount || 0))) || 0;
      totalHours += workUnits * workers;
    }
    return { data: list, totalHours };
  }

  /**
   * 获取工时记录列表（不使用缓存，直接请求）
   */
  static async getWorkTimes(params: WorkTimeQueryParams = {}): Promise<PaginatedResponse<WorkTime>> {
    try {
      const response = await api.get('/worktimes', { params });
      return response as unknown as PaginatedResponse<WorkTime>;
    } catch (error) {
      errorService.handleError(error, {
        type: ErrorType.NETWORK,
        level: ErrorLevel.ERROR,
        action: 'get_worktimes'
      }, {
        showNotification: true,
        retryable: true
      });
      throw error;
    }
  }

  /**
   * 根据ID获取工时记录详情（支持缓存）
   */
  static async getWorkTimeById(id: string): Promise<ApiResponse<WorkTime>> {
    const cacheKey = `worktime_detail_${id}`;
    
    try {
      return await cacheService.getOrSet(
        cacheKey,
        async () => {
          const response = await api.get(`/worktimes/${id}`);
          return response;
        },
        this.CACHE_TTL
      );
    } catch (error) {
      errorService.handleError(error, {
        type: ErrorType.NETWORK,
        level: ErrorLevel.ERROR,
        action: 'get_worktime_by_id'
      });
      throw error;
    }
  }

  /**
   * 创建工时记录（支持离线缓存，清除相关缓存）
   */
  static async createWorkTime(data: CreateWorkTimeData): Promise<ApiResponse<WorkTime>> {
    try {
      const response = await enhancedApi.post('/worktimes', data);
      
      // 清除相关缓存
      this.clearRelatedCache(data.projectId);
      
      return response;
    } catch (error) {
      errorService.handleError(error, {
        type: ErrorType.VALIDATION,
        level: ErrorLevel.ERROR,
        action: 'create_worktime'
      }, {
        showNotification: true,
        showMessage: true
      });
      throw error;
    }
  }

  /**
   * 更新工时记录（支持离线缓存，清除相关缓存）
   */
  static async updateWorkTime(id: string, data: UpdateWorkTimeData): Promise<ApiResponse<WorkTime>> {
    try {
      const response = await enhancedApi.put(`/worktimes/${id}`, data);
      
      // 清除相关缓存
      cacheService.delete(`worktime_detail_${id}`);
      if (data.projectId) {
        this.clearRelatedCache(data.projectId);
      }
      
      return response;
    } catch (error) {
      errorService.handleError(error, {
        type: ErrorType.VALIDATION,
        level: ErrorLevel.ERROR,
        action: 'update_worktime'
      });
      throw error;
    }
  }

  /**
   * 删除工时记录（支持离线缓存，清除相关缓存）
   */
  static async deleteWorkTime(id: string): Promise<ApiResponse> {
    try {
      const response = await enhancedApi.delete(`/worktimes/${id}`);
      
      // 清除相关缓存
      cacheService.delete(`worktime_detail_${id}`);
      this.clearListCache();
      
      return response;
    } catch (error) {
      errorService.handleError(error, {
        type: ErrorType.NETWORK,
        level: ErrorLevel.ERROR,
        action: 'delete_worktime'
      });
      throw error;
    }
  }

  /**
   * 导出工时列表Excel
   */
  static async exportExcel(params: WorkTimeQueryParams = {}): Promise<void> {
    try {
      const response = await api.get('/worktimes/export/excel', { 
        params,
        responseType: 'blob' 
      });
      // api拦截器已经返回了response.data，所以这里response就是Blob对象
      this.downloadFile(response as unknown as Blob, '工时记录列表.xlsx');
    } catch (error) {
      errorService.handleError(error, {
        type: ErrorType.NETWORK,
        level: ErrorLevel.ERROR,
        action: 'export_worktimes_excel'
      });
      throw error;
    }
  }

  /**
   * 导出工时列表PDF
   */
  static async exportPDF(params: WorkTimeQueryParams = {}): Promise<void> {
    try {
      const response = await api.get('/worktimes/export/pdf', { 
        params,
        responseType: 'blob' 
      });
      // api拦截器已经返回了response.data，所以这里response就是Blob对象
      this.downloadFile(response as unknown as Blob, '工时记录列表.pdf');
    } catch (error) {
      errorService.handleError(error, {
        type: ErrorType.NETWORK,
        level: ErrorLevel.ERROR,
        action: 'export_worktimes_pdf'
      });
      throw error;
    }
  }

  /**
   * 通用文件下载方法
   */
  private static downloadFile(data: Blob, filename: string) {
    const url = window.URL.createObjectURL(new Blob([data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  /**
   * 获取工时统计汇总（支持长时间缓存）
   */
  static async getWorkTimeSummary(params: {
    projectId?: string;
    startDate?: string;
    endDate?: string;
  } = {}): Promise<ApiResponse<WorkTimeSummary>> {
    const cacheKey = `worktime_summary_${JSON.stringify(params)}`;
    
    try {
      return await cacheService.getOrSet(
        cacheKey,
        async () => {
          const response = await api.get('/worktimes/stats/summary', { params });
          return response;
        },
        this.SUMMARY_CACHE_TTL
      );
    } catch (error) {
      errorService.handleError(error, {
        type: ErrorType.NETWORK,
        level: ErrorLevel.WARNING,
        action: 'get_worktime_summary'
      });
      throw error;
    }
  }

  /**
   * 批量创建工时记录
   */
  static async batchCreateWorkTimes(dataList: CreateWorkTimeData[]): Promise<ApiResponse<WorkTime[]>> {
    try {
      // 分批处理，避免请求过大
      const batchSize = 50;
      const results: WorkTime[] = [];
      
      for (let i = 0; i < dataList.length; i += batchSize) {
        const batch = dataList.slice(i, i + batchSize);
        const response = await api.post('/worktimes/batch', { items: batch });
        results.push(...response.data);
      }
      
      // 清除所有相关缓存
      this.clearAllCache();
      
      return {
        success: true,
        data: results,
        message: `成功创建 ${results.length} 条工时记录`
      };
    } catch (error) {
      errorService.handleError(error, {
        type: ErrorType.SERVER,
        level: ErrorLevel.ERROR,
        action: 'batch_create_worktimes'
      });
      throw error;
    }
  }

  /**
   * 获取工时记录报表数据
   */
  static async getWorkTimeReport(params: {
    projectId?: string;
    startDate?: string;
    endDate?: string;
    groupBy?: 'date' | 'project' | 'workerType';
  }): Promise<ApiResponse<any>> {
    const cacheKey = `worktime_report_${JSON.stringify(params)}`;
    
    try {
      return await cacheService.getOrSet(
        cacheKey,
        async () => {
          const response = await api.get('/worktimes/reports', { params });
          return response;
        },
        this.SUMMARY_CACHE_TTL
      );
    } catch (error) {
      errorService.handleError(error, {
        type: ErrorType.NETWORK,
        level: ErrorLevel.ERROR,
        action: 'get_worktime_report'
      });
      throw error;
    }
  }

  /**
   * 预加载常用数据
   */
  static async preloadData(projectId?: string): Promise<void> {
    try {
      const preloadTasks = [
        {
          key: `worktimes_list_${JSON.stringify({ projectId, page: 1, limit: 20 })}`,
          fetchFn: () => this.getWorkTimes({ projectId, page: 1, limit: 20 }),
          ttl: this.CACHE_TTL
        }
      ];

      if (projectId) {
        preloadTasks.push({
          key: `worktime_summary_${JSON.stringify({ projectId })}`,
          fetchFn: () => this.getWorkTimeSummary({ projectId }),
          ttl: this.SUMMARY_CACHE_TTL
        });
      }

      await cacheService.warmup(preloadTasks);
    } catch (error) {
      console.warn('预加载工时数据失败:', error);
    }
  }

  /**
   * 清除项目相关缓存
   */
  private static clearRelatedCache(projectId?: string): void {
    // 清除所有工时列表缓存（因为列表缓存键包含参数，需要清除所有）
    this.clearAllCache();
  }

  /**
   * 清除列表缓存
   */
  private static clearListCache(): void {
    // 清除所有工时相关缓存
    this.clearAllCache();
  }

  /**
   * 清除所有相关缓存
   */
  private static clearAllCache(): void {
    // 清除所有缓存以确保数据实时更新
    cacheService.clear();
  }

  /**
   * 获取缓存统计信息
   */
  static getCacheStats() {
    return cacheService.getStats();
  }
}
